import { Controller } from "@hotwired/stimulus"

// Builder Finance Settings: field autosave on change, and the A1 contract
// reader (upload → poll → review → apply).
export default class extends Controller {
  static targets = ["readerStatus", "readerResult", "readerFields", "apEmails", "apApplyAll", "arEmails", "arApplyAll"]
  static values = { updateUrl: String, presignUrl: String, readUrl: String, statusUrl: String, applyUrl: String }

  connect() {
    const msg = sessionStorage.getItem("builder-finance.flash")
    if (msg) {
      sessionStorage.removeItem("builder-finance.flash")
      this._showToast(msg)
    }
  }

  disconnect() { clearInterval(this._poll) }

  // AP emails save with their "apply to all projects" checkbox riding
  // along — the pair only makes sense together (Larry 2026-08-06).
  saveApEmails() { return this._saveAccountingEmails("ap") }

  // Accounts receivable — same shape, different column (Phelipe 2026-08-28).
  saveArEmails() { return this._saveAccountingEmails("ar") }

  async _saveAccountingEmails(side) {
    const field = side === "ap" ? "ApEmails" : "ArEmails"
    const toggle = side === "ap" ? "ApApplyAll" : "ArApplyAll"
    const body = {
      [`${side}_notification_emails`]: this[`has${field}Target`] ? this[`${side}EmailsTarget`].value : "",
      [`${side}_apply_all`]: this[`has${toggle}Target`] && this[`${side}ApplyAllTarget`].checked ? "1" : "0"
    }
    const resp = await fetch(this.updateUrlValue, {
      method: "PATCH", headers: this._headers(), body: JSON.stringify(body)
    })
    if (resp.ok) {
      this._showToast("Saved")
    } else {
      const data = await resp.json().catch(() => ({}))
      this._showToast(data.error || "Could not save", "alert")
    }
  }

  // Reminder paces (Larry item 11 / Phelipe 2026-09-03): the four dropdowns
  // and the apply-all box travel together, like the AP emails.
  async saveReminders() {
    const paces = {}
    this.element.querySelectorAll("[data-reminder-pace]").forEach((el) => { paces[el.dataset.reminderPace] = el.value })
    const all = this.element.querySelector("[data-reminder-apply-all]")
    const body = { reminder_paces: paces, reminder_apply_all: all && all.checked ? "1" : "0" }
    const resp = await fetch(this.updateUrlValue, { method: "PATCH", headers: this._headers(), body: JSON.stringify(body) })
    if (resp.ok) {
      this._showToast(all && all.checked ? "Saved for all your builder projects" : "Saved for this project")
    } else {
      const data = await resp.json().catch(() => ({}))
      this._showToast(data.error || "Could not save", "alert")
    }
  }

  async save(e) {
    const el = e.currentTarget
    const body = {}
    if (el.dataset.setting) body[el.dataset.setting] = el.value
    if (el.dataset.settingCheck) body[el.dataset.settingCheck] = el.checked ? "1" : "0"
    const resp = await fetch(this.updateUrlValue, {
      method: "PATCH", headers: this._headers(), body: JSON.stringify(body)
    })
    if (resp.ok) {
      this._showToast("Saved")
      // Settings that add/remove whole page sections (cost tracking) need a
      // refresh to show their effect.
      if (el.dataset.reload) setTimeout(() => window.location.reload(), 600)
    } else {
      const data = await resp.json().catch(() => ({}))
      this._showToast(data.error || "Could not save", "alert")
    }
  }

  // ── A1 contract reader ──
  async contractChosen(e) {
    const file = e.currentTarget.files[0]
    if (!file) return
    e.currentTarget.value = ""
    this._readerStatus(`Uploading ${file.name}…`)
    try {
      const presign = await fetch(this.presignUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      })
      const pd = await presign.json()
      if (!presign.ok) throw new Error(pd.error || "Upload refused")
      const u = pd.uploads[0]
      const put = await fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: file })
      if (!put.ok) throw new Error("Upload failed")

      this._readerStatus("Reading the contract — retainage, billing day, terms…")
      const start = await fetch(this.readUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ s3_key: `uploads/tmp/${u.cache_name}`, filename: file.name, mime_type: u.content_type })
      })
      const sd = await start.json()
      if (!start.ok) throw new Error(sd.error || "Could not start the reader")
      this._pollReader(sd.run_id)
    } catch (err) {
      this._readerStatus("")
      this._showToast(err.message, "alert")
    }
  }

  _pollReader(runId) {
    let ticks = 0
    this._poll = setInterval(async () => {
      if (++ticks > 60) { clearInterval(this._poll); this._readerStatus("Taking too long — try again."); return }
      const resp = await fetch(`${this.statusUrlValue}?run_id=${runId}`, { headers: { "Accept": "application/json" } })
      const data = await resp.json()
      if (data.status === "done") {
        clearInterval(this._poll)
        this._readerStatus("")
        this._renderExtract(data.result || {})
      } else if (data.status === "failed") {
        clearInterval(this._poll)
        this._readerStatus("")
        this._showToast(data.error || "The contract couldn't be read.", "alert")
      }
    }, 2500)
  }

  _renderExtract(extract) {
    const fields = {
      "Retainage": extract.retainage_pct ? `${extract.retainage_pct}%` : "—",
      "Billing day": extract.billing_day || "—",
      "Payment terms": extract.payment_terms_days ? `Net ${extract.payment_terms_days}` : "—",
      "State": extract.state || "—"
    }
    this.readerFieldsTarget.innerHTML = Object.entries(fields).map(([k, v]) =>
      `<div class="bg-purple-50/60 rounded-lg px-3 py-2"><span class="text-gray-500">${k}:</span> <span class="font-medium">${v}</span></div>`
    ).join("")
    this.readerResultTarget.classList.remove("hidden")
    this._showToast("Contract read — review the terms and apply")
  }

  async applyExtract() {
    try {
      const resp = await fetch(this.applyUrlValue, { method: "POST", headers: this._headers() })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not apply")
      sessionStorage.setItem("builder-finance.flash", "Terms applied — settings updated from the contract")
      window.location.reload()
    } catch (err) {
      this._showToast(err.message, "alert")
    }
  }

  _readerStatus(text) {
    this.readerStatusTarget.textContent = text
    this.readerStatusTarget.classList.toggle("hidden", !text)
  }

  _headers() {
    return {
      "Content-Type": "application/json", "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  _showToast(message, type = "notice") {
    const div = document.createElement("div")
    div.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    div.setAttribute("data-controller", "flash")
    div.setAttribute("data-flash-dismiss-after-value", "5000")
    div.textContent = message
    document.body.prepend(div)
  }
}
